import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("monk_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function fileUrl(fileId) {
  const t = localStorage.getItem("monk_token");
  return `${API}/files/${fileId}?auth=${t}`;
}

// Run a Milestone 2 forensic engine on a piece of evidence (async job + polling)
export async function runEngine(evidenceId, action, onTick) {
  const start = await api.post(`/evidence/${evidenceId}/run/${action}`);
  const jobId = start.data.job_id;
  const t0 = Date.now();
  for (let i = 0; i < 160; i++) {
    await sleep(1500);
    onTick?.(Math.round((Date.now() - t0) / 1000));
    const { data } = await api.get(`/engine-jobs/${jobId}`);
    if (data.status === "done") return data.result;
    if (data.status === "error") throw new Error(data.error || "Engine failed");
  }
  throw new Error("Engine timed out");
}

export async function enhanceImage(evidenceId) {
  const { data } = await api.post(`/evidence/${evidenceId}/enhance`);
  return data;
}

// Run a Milestone 3 case-level behavioral/geographic engine (async job + polling)
export async function runCaseEngine(caseId, action, onTick) {
  const start = await api.post(`/cases/${caseId}/run/${action}`);
  const jobId = start.data.job_id;
  const t0 = Date.now();
  for (let i = 0; i < 160; i++) {
    await sleep(1500);
    onTick?.(Math.round((Date.now() - t0) / 1000));
    const { data } = await api.get(`/engine-jobs/${jobId}`);
    if (data.status === "done") return data.result;
    if (data.status === "error") throw new Error(data.error || "Engine failed");
  }
  throw new Error("Engine timed out");
}

// Milestone 4 — generate the court-admissible PDF report (async job + polling)
export async function generateCourtReport(caseId, onTick) {
  const start = await api.post(`/cases/${caseId}/court-report`);
  const jobId = start.data.job_id;
  const t0 = Date.now();
  for (let i = 0; i < 160; i++) {
    await sleep(1500);
    onTick?.(Math.round((Date.now() - t0) / 1000));
    const { data } = await api.get(`/engine-jobs/${jobId}`);
    if (data.status === "done") return data.result;
    if (data.status === "error") throw new Error(data.error || "Court report failed");
  }
  throw new Error("Court report timed out");
}

export async function getCustody(caseId) {
  const { data } = await api.get(`/cases/${caseId}/custody`);
  return data;
}

// Edit evidence metadata (label / tags / notes). Forensic content is never touched.
export async function updateEvidence(caseId, evidenceId, patch) {
  const { data } = await api.patch(`/cases/${caseId}/evidence/${evidenceId}`, patch);
  return data;
}

// Upload a single file (returns file_id + sha256 for dedup).
export async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

// Bulk-create evidence from already-uploaded files (dedup handled server-side).
export async function addEvidenceBulk(caseId, items) {
  const { data } = await api.post(`/cases/${caseId}/evidence/bulk`, { items });
  return data;
}

// Confirm an AI-drafted triage record (clears the PENDING REVIEW state).
export async function confirmTriage(caseId, evidenceId) {
  const { data } = await api.post(`/cases/${caseId}/evidence/${evidenceId}/confirm`);
  return data;
}

// Re-extract text + re-run contextual engines for evidence missing findings.
export async function reprocessCase(caseId) {
  const { data } = await api.post(`/cases/${caseId}/reprocess`);
  return data;
}

// Natalie's high-fidelity neural voice (OpenAI TTS · Shimmer) → { audio: base64 mp3 }
export async function synthesizeSpeech(text) {
  const { data } = await api.post("/tts", { text });
  return data;
}

// Live Leads / Clue Stream + engine run pulse for the dashboard.
export async function getClues(caseId) {
  const { data } = await api.get(`/cases/${caseId}/clues`);
  return data;
}

// Re-fire the ENTIRE case-level engine array + Core Brain synthesis (the auto-pipeline).
export async function runFullArray(caseId) {
  const { data } = await api.post(`/cases/${caseId}/run-full-array`);
  return data;
}

// Restart-recovery + global background-job signal for the "resuming analyses" banner.
export async function getSystemStatus() {
  const { data } = await api.get("/system/status");
  return data;
}

// Milestone 5 — Ghost Interview streaming chat (Synthetic Killer replica, §15)
export async function streamGhost(caseId, message, onToken) {
  const token = localStorage.getItem("monk_token");
  const res = await fetch(`${API}/cases/${caseId}/ghost`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ message }),
  });
  if (!res.ok || !res.body) throw new Error("The replica is unreachable");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    onToken(decoder.decode(value, { stream: true }));
  }
}

export async function getGhostHistory(caseId) {
  const { data } = await api.get(`/cases/${caseId}/ghost/history`);
  return data;
}

// Core Brain analysis — LIVE STREAM (Flash Reporting). Streams a readable prose
// report as Monk reasons, then resolves the structured analysis. Bypasses the
// proxy's idle cap because bytes flow continuously.
function cleanFlash(s) {
  return s.replace(/^\s*FLASH REPORT:?\s*/i, "").trim();
}
export async function analyzeCaseStream(caseId, onFlash, onResult, onCompiling) {
  const token = localStorage.getItem("monk_token");
  const res = await fetch(`${API}/cases/${caseId}/analyze/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  if (!res.ok || !res.body) throw new Error("The Core Brain is unavailable");
  const jobId = res.headers.get("X-Job-Id");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const DELIM = "%%%JSON%%%";
  const RESULT = "%%%RESULT%%%";
  let buf = "";
  let compiling = false;
  let gotResult = false;
  const markCompiling = () => { if (!compiling) { compiling = true; onCompiling?.(); } };
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      if (buf.indexOf(RESULT) !== -1) { gotResult = true; markCompiling(); break; }
      const ji = buf.indexOf(DELIM);
      if (ji === -1) onFlash?.(cleanFlash(buf));
      else { onFlash?.(cleanFlash(buf.slice(0, ji))); markCompiling(); }
    }
  } catch { /* stream cut by proxy — fall back to polling the persisted result */ }
  if (gotResult) {
    const ri = buf.indexOf(RESULT);
    onResult?.(JSON.parse(buf.slice(ri + RESULT.length).trim()));
    return;
  }
  // Stream ended before the result arrived (e.g. proxy ~60s cap) — poll the job the
  // background task is still completing server-side.
  markCompiling();
  if (!jobId) throw new Error("The Core Brain returned no result");
  for (let i = 0; i < 120; i++) {
    await sleep(1500);
    const { data } = await api.get(`/cases/${caseId}/analyze/status/${jobId}`);
    if (data.status === "done") { onResult?.({ analysis: data.analysis, analyzed_at: data.analyzed_at }); return; }
    if (data.status === "error") throw new Error(data.error || "Core Brain failed");
  }
  throw new Error("The Core Brain timed out");
}
export async function streamNatalie(caseId, message, onToken) {
  const token = localStorage.getItem("monk_token");
  const res = await fetch(`${API}/cases/${caseId}/natalie`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ message }),
  });
  if (!res.ok || !res.body) throw new Error("Natalie is unavailable");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    onToken(decoder.decode(value, { stream: true }));
  }
}
